import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Avatar from 'react-avatar';
import { fetchTickerFeedThunk } from '../../redux-stores/slices/influencerSlice';
import { AppDispatch, RootState } from '../../redux-stores/store';
import './style.scss';

type InlineMarkdownRule = {
  name: string;
  /**
   * Non-global regex. The first capture group is treated as "content" by default rules.
   * The renderer will apply the earliest match (by index) across all rules repeatedly.
   */
  pattern: RegExp;
  render: (args: { match: RegExpMatchArray; key: string }) => React.ReactNode;
};

function createInlineMarkdownRenderer(rules: InlineMarkdownRule[]) {
  return function renderInlineMarkdown(input: string): React.ReactNode[] {
    if (!input) return [];

    const nodes: React.ReactNode[] = [];
    let remaining = input;
    let safety = 0;

    while (remaining.length > 0 && safety < 10_000) {
      safety += 1;

      let best:
        | {
            rule: InlineMarkdownRule;
            match: RegExpMatchArray;
            index: number;
          }
        | undefined;

      for (const rule of rules) {
        const match = remaining.match(rule.pattern);
        if (!match || typeof match.index !== 'number') continue;
        if (!best || match.index < best.index) {
          best = { rule, match, index: match.index };
        }
      }

      if (!best) {
        nodes.push(remaining);
        break;
      }

      const { rule, match, index } = best;
      if (index > 0) nodes.push(remaining.slice(0, index));

      nodes.push(rule.render({ match, key: `md-${nodes.length}` }));

      const full = match[0] ?? '';
      remaining = remaining.slice(index + full.length);
      if (full.length === 0) {
        // Avoid infinite loops if a custom rule accidentally matches empty string.
        nodes.push(remaining[0]);
        remaining = remaining.slice(1);
      }
    }

    return nodes;
  };
}

const defaultInlineMarkdownRules: InlineMarkdownRule[] = [
  {
    name: 'pro-gradient',
    pattern: /\*PRO\*/,
    render: ({ key }) => (
      <span className="marquee__pro" key={key}>
        PRO
      </span>
    ),
  },
  {
    name: 'bold',
    pattern: /\*([^*]+)\*/,
    render: ({ match, key }) => (
      <strong className="marquee__mdBold" key={key}>
        {match[1]}
      </strong>
    ),
  },
  {
    name: 'italic',
    pattern: /_([^_]+)_/,
    render: ({ match, key }) => (
      <em className="marquee__mdItalic" key={key}>
        {match[1]}
      </em>
    ),
  },
];

type MarqueeTickerProps = {
  /**
   * Pixels per second.
   * The duration is computed based on the rendered content width.
   */
  speedPxPerSecond?: number;
  /** Additional inline markdown rules (e.g. custom tags). */
  markdownRules?: InlineMarkdownRule[];
  className?: string;
  findCollabPage?: boolean;
};

const MarqueeTicker = ({ speedPxPerSecond = 60, markdownRules, className}: MarqueeTickerProps) => {
  const dispatch: AppDispatch = useDispatch();
  const feed = useSelector((state: RootState) => state.influencer.ticker.items);

  const renderInlineMarkdown = useMemo(
    () => createInlineMarkdownRenderer([...(markdownRules ?? []), ...defaultInlineMarkdownRules]),
    [markdownRules],
  );

  const groupRef = useRef<HTMLDivElement | null>(null);
  const [durationSeconds, setDurationSeconds] = useState<number>(20);

  useEffect(() => {
    dispatch(fetchTickerFeedThunk());
  }, [dispatch]);

  useLayoutEffect(() => {
    const el = groupRef.current;
    if (!el) return;

    const update = () => {
      const width = el.scrollWidth;
      if (!width || speedPxPerSecond <= 0) return;
      setDurationSeconds(Math.max(6, width / speedPxPerSecond));
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [feed, speedPxPerSecond]);

  if (!feed || feed.length === 0) return null;

  const marqueeStyle = {
    ['--marquee-duration' as any]: `${durationSeconds}s`,
  } as React.CSSProperties;

  return (
    <div className={['marquee', className].filter(Boolean).join(' ')}>
      <div className="marquee__viewport">
        <div className="marquee__track" style={marqueeStyle}>
          <div className="marquee__group" ref={groupRef} aria-hidden="false">
            {feed.map((item, idx) => (
              <div className="marquee__item" key={`ticker-1-${idx}`}>
                <div className="marquee__avatar" aria-hidden="true">
                  {item.name ? (
                    <Avatar
                      name={item.name}
                      size="20"
                      round
                      maxInitials={1}
                      textSizeRatio={2.3}
                    />
                  ) : null}
                </div>
                <p className="marquee__text">{renderInlineMarkdown(item.text)}</p>
              </div>
            ))}
          </div>

          <div className="marquee__group" aria-hidden="true">
            {feed.map((item, idx) => (
              <div className="marquee__item" key={`ticker-2-${idx}`}>
                <div className="marquee__avatar" aria-hidden="true">
                  {item.name ? (
                    <Avatar
                      name={item.name}
                      size="20"
                      round
                      maxInitials={1}
                      textSizeRatio={2.3}
                      color="#FFB7D3"
                      fgColor="#FFFFFF"
                    />
                  ) : null}
                </div>
                <p className="marquee__text">{renderInlineMarkdown(item.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarqueeTicker;